// Comprehensive offline storage utilities
export class OfflineStorage {
  private static instance: OfflineStorage;
  private dbName = 'AgroSenseDB';
  private version = 1;
  private db: IDBDatabase | null = null;

  static getInstance(): OfflineStorage {
    if (!OfflineStorage.instance) {
      OfflineStorage.instance = new OfflineStorage();
    }
    return OfflineStorage.instance;
  }

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores
        if (!db.objectStoreNames.contains('cache')) {
          const cacheStore = db.createObjectStore('cache', { keyPath: 'key' });
          cacheStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains('offline_actions')) {
          db.createObjectStore('offline_actions', { keyPath: 'id', autoIncrement: true });
        }

        if (!db.objectStoreNames.contains('user_data')) {
          db.createObjectStore('user_data', { keyPath: 'userId' });
        }
      };
    });
  }

  async set(key: string, data: any, ttl: number = 24 * 60 * 60 * 1000): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache'], 'readwrite');
      const store = transaction.objectStore('cache');

      const item = {
        key,
        data,
        timestamp: Date.now(),
        ttl,
        expires: Date.now() + ttl
      };

      const request = store.put(item);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async get<T>(key: string): Promise<T | null> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache'], 'readonly');
      const store = transaction.objectStore('cache');
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result;
        if (!result) {
          resolve(null);
          return;
        }

        // Check if expired
        if (Date.now() > result.expires) {
          this.delete(key); // Clean up expired data
          resolve(null);
          return;
        }

        resolve(result.data);
      };

      request.onerror = () => reject(request.error);
    });
  }

  async delete(key: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache'], 'readwrite');
      const store = transaction.objectStore('cache');
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clear(): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache'], 'readwrite');
      const store = transaction.objectStore('cache');
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async storeOfflineAction(action: any): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offline_actions'], 'readwrite');
      const store = transaction.objectStore('offline_actions');

      const actionWithTimestamp = {
        ...action,
        timestamp: Date.now(),
        synced: false
      };

      const request = store.add(actionWithTimestamp);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getOfflineActions(): Promise<any[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offline_actions'], 'readonly');
      const store = transaction.objectStore('offline_actions');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async markActionSynced(id: number): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offline_actions'], 'readwrite');
      const store = transaction.objectStore('offline_actions');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Cleanup expired cache entries
  async cleanup(): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache'], 'readwrite');
      const store = transaction.objectStore('cache');
      const index = store.index('timestamp');
      const request = index.openCursor();

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const item = cursor.value;
          if (Date.now() > item.expires) {
            cursor.delete();
          }
          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = () => reject(request.error);
    });
  }
}

// Utility functions for common operations
export const offlineStorage = OfflineStorage.getInstance();

export async function cacheApiResponse(endpoint: string, data: any, ttl?: number) {
  try {
    await offlineStorage.set(`api_${endpoint}`, data, ttl);
  } catch (error) {
    console.warn('Failed to cache API response:', error);
  }
}

export async function getCachedApiResponse<T>(endpoint: string): Promise<T | null> {
  try {
    return await offlineStorage.get<T>(`api_${endpoint}`);
  } catch (error) {
    console.warn('Failed to get cached API response:', error);
    return null;
  }
}

export async function storeUserData(userId: string, data: any) {
  try {
    await offlineStorage.set(`user_${userId}`, data);
  } catch (error) {
    console.warn('Failed to store user data:', error);
  }
}

export async function getUserData<T>(userId: string): Promise<T | null> {
  try {
    return await offlineStorage.get<T>(`user_${userId}`);
  } catch (error) {
    console.warn('Failed to get user data:', error);
    return null;
  }
}

// Initialize offline storage
export async function initOfflineStorage() {
  try {
    await offlineStorage.init();
    
    // Cleanup expired entries on init
    await offlineStorage.cleanup();
    
    // Setup periodic cleanup (every hour)
    setInterval(() => {
      offlineStorage.cleanup().catch(console.warn);
    }, 60 * 60 * 1000);
    
    console.log('✅ Offline storage initialized');
  } catch (error) {
    console.warn('⚠️ Offline storage initialization failed:', error);
  }
}